import type { ShapeTextSegment } from '../types/shape-text-segment'

/** Both inputs must be sorted by `startX` and non-overlapping within themselves. Exported for
 *  `shape-text-wrap-bands.ts`, which intersects a row's outline segments with the free segments
 *  left over after text-wrap exclusion bands are subtracted. */
export function intersectSegments(
  a: ShapeTextSegment[],
  b: ShapeTextSegment[],
  minWidth = 1
): ShapeTextSegment[] {
  const out: ShapeTextSegment[] = []
  let i = 0
  let j = 0

  while (i < a.length && j < b.length) {
    const startX = Math.max(a[i].startX, b[j].startX)
    const endX = Math.min(a[i].endX, b[j].endX)

    if (endX - startX >= minWidth) out.push({ startX, endX })
    if (a[i].endX < b[j].endX) i++
    else j++
  }

  return out
}

export function scanlineSegments(
  points: Array<{ x: number; y: number }>,
  y: number
): ShapeTextSegment[] {
  if (points.length < 3) return []
  const intersections: number[] = []

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]

    if (p1.y === p2.y) continue
    const minY = Math.min(p1.y, p2.y)
    const maxY = Math.max(p1.y, p2.y)

    if (y < minY || y >= maxY) continue
    const t = (y - p1.y) / (p2.y - p1.y)

    intersections.push(p1.x + (p2.x - p1.x) * t)
  }

  intersections.sort((a, b) => a - b)
  const segments: ShapeTextSegment[] = []

  for (let i = 0; i + 1 < intersections.length; i += 2) {
    const startX = intersections[i]
    const endX = intersections[i + 1]

    if (endX > startX) segments.push({ startX, endX })
  }

  return segments
}

export function applyHorizontalInset(
  segments: ShapeTextSegment[],
  insetPx: number,
  minWidth = 4
): ShapeTextSegment[] {
  return segments
    .map((s) => ({ startX: s.startX + insetPx, endX: s.endX - insetPx }))
    .filter((s) => s.endX - s.startX >= minWidth)
}

/**
 * The x-intervals a row's band cannot use because the outline's own boundary crosses them somewhere
 * inside it: each edge's swept x-extent over `[topY, botY]`, grown by `insetPx` on both sides and
 * merged.
 *
 * A glyph box occupies the WHOLE band, so an x is usable only if the vertical segment at that x
 * never crosses the outline anywhere inside the band — and every such crossing lies inside some
 * edge's swept extent. Subtracting these is therefore EXACT for a polygon, which is all
 * `flattenShapePath` ever hands us (it pre-flattens curves to line segments).
 *
 * Sampling cannot substitute for this, and two cheaper attempts were measured and rejected before
 * landing it (WMPCM-10123 round 4). Adding probe heights — even one per outline vertex inside the
 * band — still lets a *slanted* narrow notch escape: between any two probes it slides further than
 * its own width, so the sampled slices do not overlap and the swept middle goes unseen. That left
 * 4-5pt of glyph ink outside a `startwo` outline.
 *
 * `insetPx` is added here rather than applied to the result because the caller subtracts these from
 * segments that are already inset. Growing the obstacle is the same operation as insetting each
 * piece the subtraction produces, and it gives both sides of a notch the same margin off the
 * boundary that the outline's outer edges already get.
 */
export function edgeSweepBands(
  points: Array<{ x: number; y: number }>,
  topY: number,
  botY: number,
  insetPx: number
): ShapeTextSegment[] {
  const spans: ShapeTextSegment[] = []

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]
    const minY = Math.min(p1.y, p2.y)
    const maxY = Math.max(p1.y, p2.y)

    if (maxY < topY || minY > botY) continue

    let xa = p1.x
    let xb = p2.x

    // A horizontal edge sweeps its whole length; any other edge only the part inside the band.
    //
    // `t` first, then multiply — the exact order `scanlineSegments` uses. Dividing last instead
    // rounds differently in the last bit, and that was enough to shift a convex shape's row
    // `startX` by one ULP: the band's own end lands a hair PAST the segment start it should equal,
    // so the subtraction clips instead of doing nothing. Row segments are baked onto the wire
    // (`bake-shape-text-wire-lines.ts`), so "unchanged for a convex outline" has to mean bitwise.
    if (p1.y !== p2.y) {
      const tFrom = (Math.max(minY, topY) - p1.y) / (p2.y - p1.y)
      const tTo = (Math.min(maxY, botY) - p1.y) / (p2.y - p1.y)

      xa = p1.x + (p2.x - p1.x) * tFrom
      xb = p1.x + (p2.x - p1.x) * tTo
    }

    spans.push({ startX: Math.min(xa, xb) - insetPx, endX: Math.max(xa, xb) + insetPx })
  }

  spans.sort((a, b) => a.startX - b.startX)
  const merged: ShapeTextSegment[] = []

  for (const span of spans) {
    const last = merged[merged.length - 1]

    if (last && span.startX <= last.endX) last.endX = Math.max(last.endX, span.endX)
    else merged.push({ ...span })
  }

  return merged
}

/**
 * `segments` minus `bands`. Both sorted by `startX`; `bands` must also be merged and disjoint,
 * which is what `edgeSweepBands` returns — that is what lets the cursor only ever move forward.
 *
 * Same merge-then-complement shape as `computeRowFreeSegments` (`lib/text/wrap-row-layout.ts`),
 * which cannot be reused here: it works in a `[0, contentWidth]` frame over `WrapBand` objects and
 * applies a width filter of its own, where these segments carry absolute shape-local x.
 */
export function subtractSegments(
  segments: ShapeTextSegment[],
  bands: ShapeTextSegment[]
): ShapeTextSegment[] {
  if (bands.length === 0) return segments
  const out: ShapeTextSegment[] = []

  for (const segment of segments) {
    let cursor = segment.startX

    for (const band of bands) {
      if (band.endX <= cursor) continue
      if (band.startX >= segment.endX) break
      if (band.startX > cursor) out.push({ startX: cursor, endX: band.startX })
      cursor = band.endX
      if (cursor >= segment.endX) break
    }

    if (segment.endX > cursor) out.push({ startX: cursor, endX: segment.endX })
  }

  return out
}
